import _ from "underscore";

import type {
  CardId,
  CollectionId,
  DatabaseId,
  SortDirection,
  TableId,
  TransformGraphRunSortColumn,
  TransformGraphRunType,
  TransformId,
  TransformJobId,
  TransformJobRunSortColumn,
  TransformJobRunStatus,
  TransformRunMethod,
  TransformRunSortColumn,
  TransformRunStatus,
  TransformTagId,
  TransformTestId,
  TransformTestRunStatus,
} from "metabase-types/api";

const TRANSFORMS_ROOT_URL = `/data-studio/transforms`;
const JOBS_ROOT_URL = `${TRANSFORMS_ROOT_URL}/jobs`;
const RUNS_ROOT_URL = `${TRANSFORMS_ROOT_URL}/runs`;
const INDIVIDUAL_RUNS_ROOT_URL = `${RUNS_ROOT_URL}/individual`;
const LIBRARY_ROOT_URL = `${TRANSFORMS_ROOT_URL}/library`;

export type TransformPythonLibraryParams = {
  path: string;
};

export type TransformListParams = {
  collectionId?: CollectionId;
};

export function transformList({ collectionId }: TransformListParams = {}) {
  const searchParams = new URLSearchParams();
  if (collectionId != null) {
    searchParams.set("collectionId", String(collectionId));
  }

  const queryString = searchParams.toString();
  return queryString.length > 0
    ? `${TRANSFORMS_ROOT_URL}?${queryString}`
    : TRANSFORMS_ROOT_URL;
}

export function newQueryTransform() {
  return `${TRANSFORMS_ROOT_URL}/new/query`;
}

export function newNativeTransform() {
  return `${TRANSFORMS_ROOT_URL}/new/native`;
}

export function newPythonTransform() {
  return `${TRANSFORMS_ROOT_URL}/new/python`;
}

export function newTransformFromCard(cardId: CardId) {
  return `${TRANSFORMS_ROOT_URL}/new/card/${cardId}`;
}

export function transform(transformId: TransformId) {
  return `${TRANSFORMS_ROOT_URL}/${transformId}`;
}

export function transformEdit(transformId: TransformId) {
  return `${TRANSFORMS_ROOT_URL}/${transformId}/edit`;
}

export function transformRun(transformId: TransformId) {
  return `${TRANSFORMS_ROOT_URL}/${transformId}/run`;
}

export function transformSettings(transformId: TransformId) {
  return `${TRANSFORMS_ROOT_URL}/${transformId}/settings`;
}

export function transformIndexes(transformId: TransformId) {
  return `${TRANSFORMS_ROOT_URL}/${transformId}/indexes`;
}

export function transformTestList(transformId: TransformId) {
  return `${TRANSFORMS_ROOT_URL}/${transformId}/tests`;
}

export function transformTest(
  transformId: TransformId,
  testId: TransformTestId,
) {
  return `${TRANSFORMS_ROOT_URL}/${transformId}/tests/${testId}`;
}

export type TransformTestRunListParams = {
  page?: number;
  statuses?: TransformTestRunStatus[];
  startTime?: string;
  endTime?: string;
};

export function transformTestRuns(
  transformId: TransformId,
  testId: TransformTestId,
  { page, statuses, startTime, endTime }: TransformTestRunListParams = {},
) {
  const searchParams = new URLSearchParams();
  if (page != null) {
    searchParams.set("page", String(page));
  }
  statuses?.forEach((status) => {
    searchParams.append("statuses", status);
  });
  if (startTime != null) {
    searchParams.set("start-time", startTime);
  }
  if (endTime != null) {
    searchParams.set("end-time", endTime);
  }
  const queryString = searchParams.toString();
  const baseUrl = `${transformTest(transformId, testId)}/runs`;
  return queryString.length > 0 ? `${baseUrl}?${queryString}` : baseUrl;
}

export function transformDependencies(transformId: TransformId) {
  return `${TRANSFORMS_ROOT_URL}/${transformId}/dependencies`;
}

export function transformInspect(transformId: TransformId) {
  return `${TRANSFORMS_ROOT_URL}/${transformId}/inspect`;
}

export function transformMigrateModels() {
  return `${TRANSFORMS_ROOT_URL}/tools/migrate-models`;
}

export function transformJobList() {
  return JOBS_ROOT_URL;
}

export function newTransformJob() {
  return `${JOBS_ROOT_URL}/new`;
}

export function transformJob(id: TransformJobId) {
  return `${JOBS_ROOT_URL}/${id}`;
}

export type TransformJobRunListParams = {
  page?: number;
  status?: TransformJobRunStatus;
  runMethod?: TransformRunMethod;
  startTime?: string;
  sortColumn?: TransformJobRunSortColumn;
  sortDirection?: SortDirection;
};

export function transformJobRuns(
  id: TransformJobId,
  {
    page,
    status,
    runMethod,
    startTime,
    sortColumn,
    sortDirection,
  }: TransformJobRunListParams = {},
) {
  const searchParams = new URLSearchParams();
  if (page != null) {
    searchParams.set("page", String(page));
  }
  if (status != null) {
    searchParams.set("status", status);
  }
  if (runMethod != null) {
    searchParams.set("run-method", runMethod);
  }
  if (startTime != null) {
    searchParams.set("start-time", startTime);
  }
  if (sortColumn != null) {
    searchParams.set("sort-column", sortColumn);
  }
  if (sortDirection != null) {
    searchParams.set("sort-direction", sortDirection);
  }

  const queryString = searchParams.toString();
  const baseUrl = `${JOBS_ROOT_URL}/${id}/runs`;
  return queryString.length > 0 ? `${baseUrl}?${queryString}` : baseUrl;
}

export type TransformRunListParams = {
  page?: number;
  statuses?: TransformRunStatus[];
  transformIds?: TransformId[];
  transformTagIds?: TransformTagId[];
  startTime?: string;
  endTime?: string;
  runMethods?: TransformRunMethod[];
  sortColumn?: TransformRunSortColumn;
  sortDirection?: SortDirection;
};

export function transformRunList({
  page,
  transformIds,
  statuses,
  transformTagIds,
  startTime,
  endTime,
  runMethods,
  sortColumn,
  sortDirection,
}: TransformRunListParams = {}) {
  const searchParams = new URLSearchParams();
  if (page != null) {
    searchParams.set("page", String(page));
  }
  transformIds?.forEach((transformId) => {
    searchParams.append("transform-ids", String(transformId));
  });
  statuses?.forEach((status) => {
    searchParams.append("statuses", String(status));
  });
  transformTagIds?.forEach((tagId) => {
    searchParams.append("transform-tag-ids", String(tagId));
  });
  if (startTime != null) {
    searchParams.set("start-time", startTime);
  }
  if (endTime != null) {
    searchParams.set("end-time", endTime);
  }
  runMethods?.forEach((runMethod) => {
    searchParams.append("run-methods", runMethod);
  });
  if (sortColumn != null) {
    searchParams.set("sort-column", sortColumn);
  }
  if (sortDirection != null) {
    searchParams.set("sort-direction", sortDirection);
  }

  const queryString = searchParams.toString();
  if (queryString.length > 0) {
    return `${INDIVIDUAL_RUNS_ROOT_URL}?${queryString}`;
  } else {
    return INDIVIDUAL_RUNS_ROOT_URL;
  }
}

export type TransformGraphRunListParams = {
  page?: number;
  types?: TransformGraphRunType[];
  statuses?: TransformRunStatus[];
  transformIds?: TransformId[];
  startTime?: string;
  endTime?: string;
  runMethods?: TransformRunMethod[];
  sortColumn?: TransformGraphRunSortColumn;
  sortDirection?: SortDirection;
};

export function transformGraphRunList({
  page,
  types,
  statuses,
  transformIds,
  startTime,
  endTime,
  runMethods,
  sortColumn,
  sortDirection,
}: TransformGraphRunListParams = {}) {
  const searchParams = new URLSearchParams();
  if (page != null) {
    searchParams.set("page", String(page));
  }
  types?.forEach((type) => {
    searchParams.append("types", type);
  });
  statuses?.forEach((status) => {
    searchParams.append("statuses", status);
  });
  transformIds?.forEach((transformId) => {
    searchParams.append("transform-ids", String(transformId));
  });
  if (startTime != null) {
    searchParams.set("start-time", startTime);
  }
  if (endTime != null) {
    searchParams.set("end-time", endTime);
  }
  runMethods?.forEach((runMethod) => {
    searchParams.append("run-methods", runMethod);
  });
  if (sortColumn != null) {
    searchParams.set("sort-column", sortColumn);
  }
  if (sortDirection != null) {
    searchParams.set("sort-direction", sortDirection);
  }

  const queryString = searchParams.toString();
  return queryString.length > 0
    ? `${RUNS_ROOT_URL}?${queryString}`
    : RUNS_ROOT_URL;
}

export type CommonRunListParams = {
  statuses?: TransformRunStatus[];
  transformIds?: TransformId[];
  startTime?: string;
  endTime?: string;
  runMethods?: TransformRunMethod[];
};

export function pickCommonRunListParams(
  params: CommonRunListParams,
): CommonRunListParams {
  return _.pick(
    params,
    "statuses",
    "transformIds",
    "startTime",
    "endTime",
    "runMethods",
  );
}

export function transformPythonLibrary({ path }: TransformPythonLibraryParams) {
  return `${LIBRARY_ROOT_URL}/${path}`;
}

export function queryBuilderTable(tableId: TableId, databaseId: DatabaseId) {
  return `/question#?db=${databaseId}&table=${tableId}`;
}
